import BaseAPI from 'utils/api/base';

import { OpenRequest } from 'ui/types';

import { APIRequest, BackgroundOption, QueryParams, OrderingParams, SearchResponse } from 'utils/api/types';

import { env } from 'config';

import {
  HomeResponse,
  DashboardDetails,
  LocationDetailsResponse,
  VoiceMemoDetailsResponse,
  FusionAuthApplicationData,
  UploadLimitsResponse,
  Register2FAResponse,
} from './common.types';

const withShareParam = (url: string, shareId?: string): string => {
  if (!shareId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}share=${shareId}`;
};

class CommonAPI extends BaseAPI {
  logout = (): APIRequest<void> => {
    const url = '/logout/session/';
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  refreshSession = (): APIRequest<void> => {
    const url = '/session/refresh/';
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  gridRequest = <T>(
    url: string,
    { method = 'GET', body, signal }: { method?: 'GET' | 'POST'; body?: object; signal?: AbortSignal } = {}
  ): APIRequest<T> => this.request<T>({ url, method, data: body, signal });

  getUploadLimits = (): APIRequest<UploadLimitsResponse> => {
    const url = '/upload-limits/';
    const method = 'GET';

    return this.request<UploadLimitsResponse>({
      url,
      method,
    });
  };

  getFusionAuthApplicationData = (params: { username: string }): APIRequest<FusionAuthApplicationData> => {
    const url = '/fusionauth/application/';
    const method = 'GET';

    return this.request<FusionAuthApplicationData>({
      url,
      method,
      params,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  downloadAllRowsToCSV = (url: string, params?: OrderingParams | QueryParams): OpenRequest =>
    this.open({
      url,
      params,
    });

  sendExportEmail = (url: string, params?: QueryParams): APIRequest<void> => {
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      params,
    });
  };

  authWebsocket = (socketId: string, channelName: string, options?: BackgroundOption): APIRequest<{ auth: string }> => {
    const url = '/websocket/auth/';
    const method = 'POST';
    const data = {
      socketId,
      channelName,
    };

    return this.request<{ auth: string }>({
      url,
      method,
      data,
      ...options,
    });
  };

  searchGroups = (
    params?: Partial<{ query: string; organisation: string; limit: number }>
  ): APIRequest<SearchResponse[]> => {
    const url = '/groups/search/';
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  loadLocationDetails = (locationId: string): APIRequest<LocationDetailsResponse> => {
    const url = `/locations/${locationId}/`;
    const method = 'GET';

    return this.request<LocationDetailsResponse>({
      url,
      method,
    });
  };

  loadVoiceMemoDetails = (voiceMemoId: string): APIRequest<VoiceMemoDetailsResponse> => {
    const url = `/voice-memos/${voiceMemoId}/`;
    const method = 'GET';

    return this.request<VoiceMemoDetailsResponse>({
      url,
      method,
    });
  };

  resolveFileUrl = (proxyUrl: string, shareId?: string): APIRequest<{ url: string; filename: string }> =>
    this.request<{ url: string; filename: string }>({ url: withShareParam(proxyUrl, shareId), method: 'GET' });

  downloadFile = (url: string, shareId?: string): OpenRequest => {
    // Check if this is a proxy URL that needs authenticated fetch
    const isProxyUrl = url.includes('files/') && url.includes('/download/');

    if (!isProxyUrl) return this.open({ url, finalURL: url });

    const download = (async (): Promise<void> => {
      const response = await this.request<{ url: string; filename: string }>({
        url: withShareParam(url, shareId),
        method: 'GET',
      })();

      window.open(response.data.url);
    }) as unknown as OpenRequest;

    download.config = { url, finalURL: url };
    return download;
  };

  loadDashboard = (dashboardId: string): APIRequest<DashboardDetails> => {
    const url = `/dashboards/${dashboardId}/`;
    const method = 'GET';

    return this.request<DashboardDetails>({
      url,
      method,
    });
  };

  updateDashboard = (dashboardId: string, data: Partial<DashboardDetails>): APIRequest<DashboardDetails> => {
    const url = `/dashboards/${dashboardId}/`;
    const method = 'PATCH';

    return this.request<DashboardDetails>({
      url,
      method,
      data,
    });
  };

  verifyPhoneNumber = (data: { phoneNumber: string }, authorization?: string): APIRequest<void> => {
    const url = '/verifications/phone/2fa/';
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      data,
      authorization,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  confirmVerificationCode = (
    data: { phoneNumber: string; verificationCode: string },
    authorization?: string
  ): APIRequest<Register2FAResponse> => {
    const url = '/register/2fa/';
    const method = 'POST';

    return this.request<Register2FAResponse>({
      url,
      method,
      data,
      authorization,
      baseURL: env.REACT_APP_ADMIN_API_URL,
    });
  };

  loadHomeContent = (): APIRequest<HomeResponse> => {
    const url = '/home/';
    const method = 'GET';

    return this.request<HomeResponse>({
      url,
      method,
    });
  };
}

export default CommonAPI;
